import { useCallback, useEffect, useRef, useState } from "react";

import { api } from "../api/client";

interface EmployeeResult {
  id: number;
  first_name: string;
  last_name: string;
  site_name?: string;
}

interface EmployeeSearchProps {
  value: number | null;
  onChange: (id: number | null) => void;
  placeholder?: string;
  emptyLabel?: string;
  className?: string;
  disabled?: boolean;
  excludeIds?: number[];
}

interface EmployeeSearchMultiProps {
  values: number[];
  onChange: (ids: number[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  excludeIds?: number[];
}

export default function EmployeeSearch({
  value,
  onChange,
  placeholder = "Search employees...",
  emptyLabel = "-- None --",
  className = "input",
  disabled = false,
  excludeIds = [],
}: EmployeeSearchProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<EmployeeResult[]>([]);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!value) {
      setSelectedLabel(null);
      return;
    }
    void api
      .get<EmployeeResult[]>(`/api/employees?is_active=true`)
      .then((list) => {
        const found = list.find((e) => e.id === value);
        if (found) {
          setSelectedLabel(
            `${found.first_name} ${found.last_name}${found.site_name ? ` (${found.site_name})` : ""}`,
          );
        }
      })
      .catch(() => {});
  }, [value]);

  const search = useCallback(
    (q: string) => {
      if (!q.trim()) {
        setResults([]);
        return;
      }
      void api
        .get<EmployeeResult[]>(
          `/api/employees?is_active=true&search=${encodeURIComponent(q)}`,
        )
        .then((list) => {
          setResults(
            list
              .filter((e) => !excludeIds.includes(e.id))
              .slice(0, 20)
              .map((e) => ({
                id: e.id,
                first_name: e.first_name,
                last_name: e.last_name,
                site_name: e.site_name,
              })),
          );
        })
        .catch(() => setResults([]));
    },
    [excludeIds],
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, search]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const select = (emp: EmployeeResult | null) => {
    if (emp) {
      onChange(emp.id);
      setSelectedLabel(
        `${emp.first_name} ${emp.last_name}${emp.site_name ? ` (${emp.site_name})` : ""}`,
      );
    } else {
      onChange(null);
      setSelectedLabel(null);
    }
    setOpen(false);
    setQuery("");
  };

  if (disabled) {
    return (
      <div className={className + " bg-gray-50 text-gray-500"}>
        {selectedLabel ?? emptyLabel}
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen(!open);
          if (!open) setTimeout(() => inputRef.current?.focus(), 0);
        }}
        className={
          className + " flex w-full items-center justify-between text-left"
        }
      >
        <span className={selectedLabel ? "text-gray-900" : "text-gray-500"}>
          {selectedLabel ?? emptyLabel}
        </span>
        <span className="text-xs text-gray-400">▾</span>
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-lg border border-gray-200 bg-white shadow-lg">
          <div className="border-b border-gray-100 p-2">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={placeholder}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-pme-blue focus:outline-none focus:ring-1 focus:ring-pme-blue"
            />
          </div>
          <div className="max-h-48 overflow-y-auto">
            <button
              type="button"
              onClick={() => select(null)}
              className="w-full px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-50"
            >
              {emptyLabel}
            </button>
            {results.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => select(r)}
                className={
                  "w-full px-3 py-2 text-left text-sm hover:bg-gray-50" +
                  (r.id === value
                    ? " bg-blue-50 font-medium text-pme-blue"
                    : " text-gray-700")
                }
              >
                {r.first_name} {r.last_name}
                {r.site_name && (
                  <span className="ml-1 text-xs text-gray-400">
                    ({r.site_name})
                  </span>
                )}
              </button>
            ))}
            {results.length === 0 && query.trim() && (
              <p className="px-3 py-2 text-sm text-gray-400">No results</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function EmployeeSearchMulti({
  values,
  onChange,
  placeholder = "Search employees...",
  className = "input",
  disabled = false,
  excludeIds = [],
}: EmployeeSearchMultiProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<EmployeeResult[]>([]);
  const [chips, setChips] = useState<
    { id: number; label: string }[]
  >([]);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (values.length === 0) {
      setChips([]);
      return;
    }
    void api
      .get<EmployeeResult[]>(`/api/employees?is_active=true`)
      .then((list) => {
        setChips(
          values
            .map((id) => {
              const e = list.find((emp) => emp.id === id);
              if (!e) return null;
              return {
                id,
                label: `${e.first_name} ${e.last_name}${e.site_name ? ` (${e.site_name})` : ""}`,
              };
            })
            .filter(Boolean) as { id: number; label: string }[],
        );
      })
      .catch(() => {});
  }, [values]);

  const search = useCallback(
    (q: string) => {
      if (!q.trim()) {
        setResults([]);
        return;
      }
      void api
        .get<EmployeeResult[]>(
          `/api/employees?is_active=true&search=${encodeURIComponent(q)}`,
        )
        .then((list) => {
          setResults(
            list
              .filter((e) => !values.includes(e.id) && !excludeIds.includes(e.id))
              .slice(0, 20)
              .map((e) => ({
                id: e.id,
                first_name: e.first_name,
                last_name: e.last_name,
                site_name: e.site_name,
              })),
          );
        })
        .catch(() => setResults([]));
    },
    [values, excludeIds],
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 200);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, search]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const addEmployee = (emp: EmployeeResult) => {
    const label = `${emp.first_name} ${emp.last_name}${emp.site_name ? ` (${emp.site_name})` : ""}`;
    setChips((prev) => [...prev, { id: emp.id, label }]);
    onChange([...values, emp.id]);
    setQuery("");
    setResults([]);
    inputRef.current?.focus();
  };

  const removeEmployee = (id: number) => {
    setChips((prev) => prev.filter((c) => c.id !== id));
    onChange(values.filter((v) => v !== id));
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div
        className={
          className +
          " flex min-h-[2.5rem] flex-wrap items-center gap-1 cursor-text"
        }
        onClick={() => {
          setOpen(true);
          inputRef.current?.focus();
        }}
      >
        {chips.map((c) => (
          <span
            key={c.id}
            className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800"
          >
            {c.label}
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeEmployee(c.id);
                }}
                className="text-blue-600 hover:text-blue-800"
              >
                ×
              </button>
            )}
          </span>
        ))}
        {!disabled && (
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={chips.length === 0 ? placeholder : ""}
            className="min-w-[120px] flex-1 border-0 bg-transparent p-0 text-sm outline-none focus:ring-0"
            disabled={disabled}
          />
        )}
      </div>

      {open && results.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
          {results.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => addEmployee(r)}
              className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
            >
              {r.first_name} {r.last_name}
              {r.site_name && (
                <span className="ml-1 text-xs text-gray-400">
                  ({r.site_name})
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      {open && query.trim() && results.length === 0 && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
          <p className="text-sm text-gray-400">No results</p>
        </div>
      )}
    </div>
  );
}
